import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import RootState from "../../../redux/states/root.state";
import useDebounce from "../../../hooks/useDebounce";
import useWeatherLocation from "../../../hooks/useWeatherLocation";
import {
  getWeather,
  searchLocations,
  LocationResult,
  TempUnits,
  Weather,
} from "../../../services/api/weather.api";
import { unitSymbols, weatherIcon } from "../../../utils/weatherConditions";

const UNIT_KEY = "temperature_unit";

/* Deliberately separate from `temperatureUnit` in utils/temperature.ts, which drives spa
   temperatures — weather units are their own preference here. METRIC/IMPERIAL are the values
   the old Tomorrow.io widget wrote, migrated on read so existing dealers keep their choice. */
const readUnits = (): TempUnits => {
  const stored = localStorage.getItem(UNIT_KEY);

  if (stored === "imperial" || stored === "IMPERIAL") return "imperial";
  return "metric";
};

const locationLabel = (hit: LocationResult) =>
  [hit.name, hit.state, hit.country].filter(Boolean).join(", ");

const WeatherCard: React.FC = () => {
  const token = useSelector((state: RootState) => state.user.accessToken);

  const { location, setLocation, requestPrecise, locating, geoError, dismissError } =
    useWeatherLocation();

  const [units, setUnits] = useState<TempUnits>(readUnits);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /* Bumped by the retry button to re-run the fetch effect without touching location or units. */
  const [attempt, setAttempt] = useState(0);

  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<LocationResult[]>([]);
  const debouncedQuery = useDebounce(query, 350);

  /* No theme listener here on purpose. The old widget tracked `data-theme` in JS because the
     third-party iframe could not read our CSS; this card is styled with the same custom
     properties as every other tile, and App.tsx already swaps the MUI theme, so light/dark
     follows automatically. That also removes the leaking `storage` listener it used to add. */

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    getWeather(token, location.lat, location.lng, units, controller.signal)
      .then((data) => {
        setWeather(data);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err?.response?.data?.data?.message ?? "Weather is unavailable right now.");
        setLoading(false);
      });

    return () => controller.abort();
    // `attempt` is a deliberate trigger-only dependency — the retry button bumps it to re-run
    // this fetch without changing the location or units it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.lat, location.lng, units, attempt]);

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();

    searchLocations(token, debouncedQuery.trim(), controller.signal)
      .then(setOptions)
      .catch(() => {
        if (!controller.signal.aborted) setOptions([]);
      });

    return () => controller.abort();
  }, [token, debouncedQuery]);

  const handleUnitChange = useCallback((_: unknown, next: TempUnits | null) => {
    if (!next) return; // ToggleButtonGroup emits null when the active button is re-clicked.

    setUnits(next);
    localStorage.setItem(UNIT_KEY, next);
  }, []);

  const symbols = useMemo(() => unitSymbols(units), [units]);

  const place = weather?.place ?? location.label ?? "Weather";
  const CurrentIcon = weatherIcon(weather?.current.code ?? 800, weather?.current.is_day ?? true);

  /* Keeps the input from being cleared while the user is still typing a new search. */
  const inputRef = useRef<HTMLInputElement | null>(null);

  const temp = (value: number | null | undefined) =>
    value === null || value === undefined ? "--" : `${Math.round(value)}°`;

  return (
    <div className="dash-card-container" id="weather-card">
      <div className="weather-head">
        <span className="weather-place" title={place}>
          {place}
          {weather?.country ? `, ${weather.country}` : ""}
        </span>

        <div className="weather-controls">
          <Autocomplete<LocationResult, false, false, true>
            size="small"
            freeSolo
            className="weather-search"
            options={options}
            filterOptions={(x) => x} // Already filtered server-side; keep every result.
            getOptionLabel={(option) =>
              typeof option === "string" ? option : locationLabel(option)
            }
            isOptionEqualToValue={(a, b) => a.lat === b.lat && a.lng === b.lng}
            onInputChange={(_, value) => setQuery(value)}
            /* OpenWeather's geocoder matches complete city names only — "Edmont" returns
               nothing where "Edmonton" returns five. Saying so beats an empty dropdown that
               reads as broken. */
            noOptionsText={
              query.trim().length < 2 ? "Type a city name" : "No match — try the full city name"
            }
            onChange={(_, value) => {
              if (!value || typeof value === "string") return;

              setLocation({ lat: value.lat, lng: value.lng, label: locationLabel(value) });
              setQuery("");
              inputRef.current?.blur();
            }}
            renderInput={(params) => (
              <TextField {...params} inputRef={inputRef} placeholder="Search city" />
            )}
          />

          <Tooltip title="Use my location">
            <span>
              <IconButton
                size="small"
                onClick={requestPrecise}
                disabled={locating}
                aria-label="Use my location"
              >
                {locating ? <CircularProgress size={18} /> : <MyLocationIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={units}
            onChange={handleUnitChange}
            aria-label="Temperature units"
          >
            <ToggleButton value="metric" aria-label="Celsius">°C</ToggleButton>
            <ToggleButton value="imperial" aria-label="Fahrenheit">°F</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      {geoError && (
        <Alert severity="info" onClose={dismissError} className="weather-alert">
          {geoError}
        </Alert>
      )}

      {loading ? (
        <div className="weather-state">
          <CircularProgress size="40px" />
        </div>
      ) : error ? (
        <div className="weather-state">
          <p>{error}</p>
          <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        </div>
      ) : (
        weather && (
          <>
            <div className="weather-now">
              <CurrentIcon className="weather-now-icon" />

              <div className="weather-now-temp">
                <strong>{temp(weather.current.temp)}</strong>
                <span>{weather.current.label}</span>
              </div>

              <dl className="weather-stats">
                <div>
                  <dt>Feels like</dt>
                  <dd>{temp(weather.current.feels_like)}</dd>
                </div>
                <div>
                  <dt>Wind</dt>
                  <dd>
                    {weather.current.wind ?? "--"} {symbols.wind}
                  </dd>
                </div>
                <div>
                  <dt>Humidity</dt>
                  <dd>{weather.current.humidity ?? "--"}%</dd>
                </div>
              </dl>
            </div>
          </>
        )
      )}

      {/* Required by OpenWeather's ODbL terms on the free plan — not decoration. */}
      <a
        className="weather-attribution"
        href="https://openweathermap.org/"
        rel="nofollow noopener noreferrer"
        target="_blank"
      >
        Weather data provided by OpenWeather
      </a>
    </div>
  );
};

export default WeatherCard;
